import { Injectable } from '@angular/core';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { RxIntuitiveCrownAdditionalInfoRulesService } from '@modules/rx-intuitive-crown-additional-info/services/rx-intuitive-crown-additional-info-rules.service';
import { IntuitiveToothForTreatment } from '../../models';
import { indicationsWithoutDetails } from '../../models/consts';
import { RxIntuitiveIndicationMapperService } from '../rx-intuitive-indication-mapper.service';
import { RxIntuitiveTeethHelperService } from '../rx-intuitive-teeth-helper.service';
import { RxIntuitiveTeethDiagramQuery } from '../../state/rx-intuitive-teeth-diagram-query';
import { RxIntuitiveToothTreatmentMapper } from '../rx-intuitive-tooth-treatment-mapper.service';

/**
 * Owns all view / query observables for the tooth editor panel.
 *
 * Both {@link RxIntuitiveToothEditorFacade} and
 * {@link RxIntuitiveToothEditorBridgeFacade} compose this service so that the
 * same derived observables are not duplicated across the two thin facades.
 */
@Injectable()
export class RxIntuitiveToothEditorViewService {
	/** Observable of the flat teeth array from the persisted diagram store. */
	readonly teethFlat$ = this.teethDiagramQuery.teethFlat$;

	/** Active teeth numbering system (ADA / FDI / Palmer). */
	readonly teethNumberingSystem$ = this.teethHelper.teethNumberingSystem$;

	/** Whether the side tooth editor panel should be visible. */
	readonly isToothEditorShown$: Observable<boolean> = this.teethHelper.isToothEditorShown$;

	/** Preview treatment list items for the sidebar list. */
	readonly previewTreatmentListItems$ = this.teethHelper.previewTreatmentListItems$;

	/**
	 * Teeth currently shown in the tooth editor: selected teeth mapped to
	 * treatment view models, sorted by lastTouchedAt desc then toothId asc.
	 */
	readonly editorTeeth$: Observable<IntuitiveToothForTreatment[]> = combineLatest([
		this.teethHelper.selectedTeeth$,
		this.teethHelper.teethNumberingSystem$,
		this.intuitiveQuery.toothLastTouchedAt$
	]).pipe(
		map(([selectedTeeth, teethNumberingSystem, toothLastTouchedAt]) =>
			selectedTeeth
				.filter(t => !this.indicationMapper.isDefaultUnitType(t.UnitTypeID))
				.map(t => this.toothTreatmentMapper.toIntuitiveToothForTreatment(t, teethNumberingSystem, toothLastTouchedAt, true))
				.sort((a, b) => {
					if (b.lastTouchedAt !== a.lastTouchedAt) {
						return b.lastTouchedAt - a.lastTouchedAt;
					}

					return a.toothId - b.toothId;
				})
		),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	constructor(
		private readonly teethHelper: RxIntuitiveTeethHelperService,
		private readonly intuitiveQuery: RxIntuitiveTeethDiagramQuery,
		private readonly teethDiagramQuery: TeethDiagramQuery,
		private readonly indicationMapper: RxIntuitiveIndicationMapperService,
		private readonly toothTreatmentMapper: RxIntuitiveToothTreatmentMapper,
		private readonly additionalInfoRules: RxIntuitiveCrownAdditionalInfoRulesService
	) {}

	/**
	 * Returns `true` when the additional info "saved" badge should be shown on
	 * the additional info button.
	 */
	hasAdditionalInfoButtonSaved(teethFlat: readonly Tooth[], sessionToothIds: readonly number[]): boolean {
		return this.additionalInfoRules.hasAdditionalInfoButtonSaved(teethFlat, sessionToothIds);
	}

	/**
	 * Returns `true` when the tooth editor details panel should be shown for
	 * the given set of editor teeth.
	 *
	 * Replaces the `protected computeCanEditToothEditor` helper that existed on
	 * the base facade — now public since there is no protected surface in a
	 * composed service.
	 */
	computeCanEditToothEditor(editorTeeth: IntuitiveToothForTreatment[]): boolean {
		const teethWithIndication = editorTeeth.filter(t => !this.indicationMapper.isDefaultIndication(t.indicationId));

		if (teethWithIndication.length === 0) {
			return true;
		}

		return !indicationsWithoutDetails.includes(teethWithIndication[0].indicationId);
	}
}
